import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Footer from './components/Footer';
import Header from './components/Header';
import MobileMenu from './components/MobileMenu';
import MobileFooter from './components/MobileFooter';
import FloatingAdminButton from './components/FloatingAdminButton';
import Contact from './pages/Contact';
import Content from './pages/Content';
import Info from "./pages/Info";
import Introduction from './pages/Introduction';
import About from './pages/About';
import Login from './components/Login';
import { AuthProvider } from './context/AuthContext';
import AdminEditor from './pages/AdminEditor';
import UserContact from './pages/UserContact';
import ResourceManager from './pages/ResourceManager';
import SEO from './components/SEO';

import companyService from './services/companyService';
import homeService from './services/homeService';
import serviceService from './services/serviceService';
import serviceTypeService from './services/serviceTypeService';

const App = () => {

    // Load data via Lambda API
    const [companyData, setCompanyData] = useState({});
    const [homeData, setHomeData] = useState({});
    const [serviceData, setServiceData] = useState([]);
    const [serviceTypeData, setServiceTypeData] = useState([]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [c, h, s, st] = await Promise.all([
                    companyService.getObjectData().catch(() => ({})),
                    homeService.getObjectData().catch(() => ({})),
                    serviceService.getListData().catch(() => []),
                    serviceTypeService.getListData().catch(() => []),
                ]);
                if (!alive) return;
                setCompanyData(c || {});
                setHomeData(h || {});
                setServiceData(Array.isArray(s) ? s : []);
                setServiceTypeData(Array.isArray(st) ? st : []);
            } catch (_) {
                if (!alive) return;
                setCompanyData({});
                setHomeData({});
                setServiceData([]);
                setServiceTypeData([]);
            }
        })();
        return () => { alive = false; };
    }, []);

    return (
        <AuthProvider>
            <Router>
                <AppContent 
                    companyData={companyData}
                    homeData={homeData}
                    serviceData={serviceData}
                    serviceTypeData={serviceTypeData}
                />
            </Router>
        </AuthProvider>
    );
};

// Separate component to use useLocation hook
const AppContent = ({ companyData, homeData, serviceData, serviceTypeData }) => {
    const location = useLocation();
    
    // Set default SEO for homepage
    const isHomePage = location.pathname === '/';
    
    return (
        <>
            {isHomePage && (
                <SEO
                    title="香港留学申请 - 留洋教育科技公司 | 專業香港留學服務 | 香港大學申請"
                    description="香港留学申请服务 - 留洋教育科技公司提供專業香港留學申請、香港大學申請、香港研究生申請等一站式服務。協助申請香港副學士、本科、研究生課程，提供簽證辦理、IANG簽證申請等全程支持。專業團隊，成功案例豐富，助您實現香港留學夢想。同時提供美國、新加坡留學申請及移民服務。"
                    keywords="香港留学,香港留學,香港留学申请,香港大學申請,香港研究生申請,香港副學士,香港本科,香港留學服務,留學申請,移民服務,美國留學,新加坡留學,香港優才計劃,香港專才計劃,留學規劃,移民顧問"
                    canonical="https://liuyanghorizon.com/"
                    ogImage="https://liuyanghorizon.com/images/logo.png"
                />
            )}
            <Header companyInfo={companyData} servicesInfo={serviceTypeData} />
            <FloatingAdminButton />
            <Routes>
                <Route path="/" element={<Content
                    page={homeData}
                    rootClassName="home-page"
                    sectionPrefix="home-section"
                    notFoundText=""
                />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/about" element={<About />} />
                    {serviceData.map((value) => {
                        // Build a mapping from ":type" to the child's content, e.g. "hkHigh" -> content
                        const childrenToContentMap = (children = []) => {
                            return children.reduce((acc, child) => {
                                const lastSeg = (child.toURL || '').split('/').pop();
                                if (lastSeg) acc[lastSeg] = child.content;
                                return acc;
                            }, {});
                        };

                        return (
                            <React.Fragment key={value.id}>
                                <Route
                                    path={`/${value.id}`}
                                    element={<Introduction toDisplay={value.id} serviceData={serviceData} />}
                                />
                                <Route
                                    path={`/${value.id}/:type`}
                                    element={
                                        <Content
                                            data={childrenToContentMap(value.children)}
                                            rootClassName={`${value.id}-generic-page`}
                                            sectionPrefix={`${value.id}-section`}
                                            notFoundText="Page not found"
                                        />
                                    }
                                />
                            </React.Fragment>
                        );
                    })}
                    <Route path="/introduction" element={<Introduction serviceData={serviceData} />} />
                    <Route path="/info" element={<Info />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/admin" element={<AdminEditor />} />
                    <Route path="/user-contacts" element={<UserContact />} />
                    <Route path="/resource-manager" element={<ResourceManager />} />
            </Routes>
            <MobileFooter companyInfo={companyData} />
            <MobileMenu servicesInfo={serviceData} />
            <Footer companyInfo={companyData} />
        </>
    );
};

export default App;